// @flow
import React, { useState, useEffect } from 'react';
import type { Element } from 'react';
import { connect } from 'react-redux';
import compose from 'lodash/fp/compose';
import { NextSeo } from 'next-seo';
import omit from 'lodash/omit';
import {
  FINDER_PACKAGE, YAPITA_HEALTH_URL, CUREFY_URL
} from '@routes';
import { withTranslation, Router, nextRoutes } from '@i18n';
import type { GlobalState } from '@globalTypes';

import { sortQueryParams } from '@lib/helpers';
import { requestPackageList, resetPackageList, updatePackageList } from './redux/actions';
import SearchResultList from './SearchResultList/index';
import {
  StyledMainCover,
  StyledHeadlineText,
  StyledGridUnit,
  StyledPagination,
  StyledResult,
  Headline,
  StyledH1Container,
  StyledResetFilter
} from './styles';
import type { Props } from './types';
import SearchBar from './SearchBar/index';
import { ITEMS_PER_PAGE } from './redux/constants';


const PackageFinder = ({
  requestingPackageList,
  resetPackageList,
  query,
  curefyDomain,
  t,
  packageList,
  numberOfPages,
  SearchList
}: Props): Element<any> => {
  const [isFirstLoad, setIsFirstLoad] = useState(true);
  const totalItems = (numberOfPages || 0) * ITEMS_PER_PAGE;

  const { locale } = nextRoutes;
  const listSize: number = numberOfPages > 1 ? totalItems : packageList.length;
  useEffect((): (() => void) => resetPackageList, []);
  useEffect((): void => {
    if (isFirstLoad) setIsFirstLoad(false);
    if (!isFirstLoad) {
      requestPackageList(query);
    }
  }, [query]);

  const { country, pageNumber, treatment_id } = query;

  const generateUrl = () => {
    let baseUrl = `${curefyDomain ? CUREFY_URL : YAPITA_HEALTH_URL}/${locale}/packages`;
    if (treatment_id) {
      baseUrl += `?treatment_id=${treatment_id}`;
    }
    if (pageNumber) {
      if (baseUrl.includes('?')) {
        baseUrl += `&pageNumber=${pageNumber}`;
      } else {
        baseUrl += `?pageNumber=${pageNumber}`;
      }
    }

    return baseUrl;
  };

  const setPage = (pageNumber: number): void => {
    const params = sortQueryParams({ ...omit(query, 'nextRoute'), pageNumber }, [
      'treatment_id',
      'country',
      'pageNumber'
    ]);
    Router.pushRoute(FINDER_PACKAGE, params);
  };
  const queryPageNumber = parseInt(query?.pageNumber, 10) || 1;
  const multiple = listSize > 1;
  const treatmentPackage = SearchList
                    && SearchList.find(obj => obj?.attributes?.id === treatment_id);
  const treatmentName = treatmentPackage?.attributes?.name || '';
  const treatmentHeadLineName = treatmentPackage ? `${treatmentName}` : '';

  const metaData: {
  MetaTitle: string,
  MetaDescription: string,
  } = t('Seo:PackageListing', {
    returnObjects: true,
    treatment: treatmentName,
    country: country || 'the world'
  });

  const HeadLines: {
  Facilities: string,
    } = t('Facilities:Headlines', {
      returnObjects: true,
      department: treatmentHeadLineName,
      city: country || 'the World',
      facility: 'Packages'
    });

  const clearFilters = () => {
    Router.pushRoute(FINDER_PACKAGE, { pageNumber: 1 });
  };

  return (
    <>
      <NextSeo
        title={`${metaData.MetaTitle}| ${curefyDomain ? 'Curefy' : 'Yapita Health'}`}
        description={`${metaData.MetaDescription} | ${curefyDomain ? 'Curefy' : 'Yapita Health'}`}
        canonical={generateUrl()}
        openGraph={{
          url: generateUrl(),
          title: `${metaData.MetaTitle} - ${curefyDomain ? 'Curefy' : 'Yapita Health'}`,
          description: `${metaData.MetaDescription} - ${curefyDomain ? 'Curefy' : 'Yapita Health'}`,
          images: [
            {
              url:
                'https://yapita-production.s3.ap-south-1.amazonaws.com/images/yapita-health-og.jpg',
              width: 800,
              height: 600,
              alt: 'Yapita Health - Your Medical Partner',
              type: 'image/jpg'
            }
          ],
          siteName: curefyDomain ? 'Curefy' : 'Yapita Health'
        }}
        twitter={{
          handle: '@yapitahealth',
          site: '@yapitahealth',
          cardType: 'summary_large_image'
        }}
      />

      <StyledMainCover>
        <StyledHeadlineText>
          <StyledGridUnit size={{ mobile: 1 / 1, desktop: 2 / 5 }}>
            <Headline>
              <StyledH1Container>
                { HeadLines.Facilities}
              </StyledH1Container>
            </Headline>
          </StyledGridUnit>
          <StyledGridUnit size={{ mobile: 1 / 1, desktop: 3 / 5 }}>
            <SearchBar
              query={omit(query, 'nextRoute')}
              numberOfPages={numberOfPages}
              objectList={packageList}
              country={query.country}
              position={query.position}
              treatment_id={query.treatment_id}
            />
          </StyledGridUnit>
        </StyledHeadlineText>
        <StyledResult>
          {t('results', { noOfPackages: listSize, s: multiple ? 's' : '' })}
          {(query.treatment_id || query.country) && (
          <StyledResetFilter onClick={clearFilters}>
            reset filter
          </StyledResetFilter>
          )}
        </StyledResult>
        <SearchResultList
          loading={requestingPackageList}
          objectList={packageList}
          position={query.position}
          country={query.country}
          numberOfPages={numberOfPages}
        />
        <StyledPagination
          initialPage={queryPageNumber - 1}
          numberOfPages={numberOfPages}
          setPage={setPage}
          pathname="/finder/packages"
        />
      </StyledMainCover>
    </>
  );
};

PackageFinder.getInitialProps = async ({ query, store }) => {
  store.dispatch(requestPackageList(query));
  const { getState } = store;
  const { page } = getState();

  const { curefyDomain } = page;

  return {
    namespacesRequired: ['ProfilePackages'],
    query,
    curefyDomain
  };
};

const mapStateToProps = (state: GlobalState): {} => ({
  numberOfPages: state.packageFinder.numberOfPages,
  packageList: state.packageFinder.data,
  requestingPackageList: state.packageFinder.getStatus?.requesting,
  SearchList: state.packageFinder.searchData
});

export default compose(
  connect(
    mapStateToProps,
    {
      requestPackageList,
      updatePackageList,
      resetPackageList
    }
  ),
  withTranslation(['ProfilePackages'])
)(PackageFinder);
